/**
 * PUKO Plinko ball-drop game (v5 — compositor-driven ball, true free-fall feel).
 *
 * Why v5: v4 animated an SVG circle via rAF + setAttribute — main-thread bound,
 * so heavy pages (vendor.js, analytics, A/B tools) made the drop stutter, and
 * per-segment easing reset gave a stop-start rhythm instead of gravity.
 * v5 pre-computes the whole trajectory into ONE CSS @keyframes animation on an
 * HTML ball element (transform-only): it runs on the compositor thread (immune
 * to main-thread jank) and keyframe times are sqrt(y)-spaced, i.e. constant
 * acceleration — a genuine free-fall rhythm.
 *
 * Gameplay: player clicks "Drop Ball" (one drop per popup-open). Slots display
 * 10% | 15% | 20% | 25% | 30%. The win drives claim headline, form
 * contact[tags] and sessionStorage.pukoPlinkoWon (read by the post-submit
 * thank-you paths to show the matching WELCOME code).
 * No-JS fallback: claim panel is visible by default.
 */
(function () {
  var POPUP_SELECTOR = '#halo-newsletter-popup, [data-newsletter-popup]';
  var OPEN_CLASS = 'newsletter-show';
  var SLOT_VALUES = ['10%', '15%', '20%', '25%', '30%'];
  // All five slots are winnable, weighted (sums to 100):
  var WINNABLE = [
    { index: 0, pct: '10', weight: 6,  tag: 'newsletter,puko-welcome10' },
    { index: 1, pct: '15', weight: 43, tag: 'newsletter,puko-welcome15' },
    { index: 2, pct: '20', weight: 35, tag: 'newsletter,puko-welcome20' },
    { index: 3, pct: '25', weight: 12, tag: 'newsletter,puko-welcome25' },
    { index: 4, pct: '30', weight: 4,  tag: 'newsletter,puko-welcome30' }
  ];
  function pickWin() {
    var total = 0, i;
    for (i = 0; i < WINNABLE.length; i++) total += WINNABLE[i].weight;
    var r = Math.random() * total;
    for (i = 0; i < WINNABLE.length; i++) { r -= WINNABLE[i].weight; if (r < 0) return WINNABLE[i]; }
    return WINNABLE[1];
  }
  // Classic Plinko triangle: apex (2 pegs) under the ball, one more peg per
  // row, constant pitch — pegs themselves draw the triangle.
  var ROW_PEGS = [2, 3, 4, 5, 6, 7];
  var PEG_PITCH = 38;
  var FALL_MS = 1900;      // free-fall portion
  var BOARD_W = 300;
  var BOARD_H = 230;
  var ROW_HEIGHT = 26;
  var TOP_OFFSET = 34;
  var START_Y = 10;
  var LAND_Y = 205;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function isSuccessStateActive() {
    if (document.body.classList.contains('newsletter-message-show')) return true;
    try {
      if (window.sessionStorage.getItem('pukoNewsletterSubmitted') === 'true') return true;
    } catch (e) {}
    if (/newsletter&form_type=customer/.test(window.location.href)) return true;
    return false;
  }

  function buildBoardSVG() {
    var svgNS = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('viewBox', '0 0 ' + BOARD_W + ' ' + BOARD_H);
    svg.setAttribute('class', 'puko-plinko-board');
    svg.setAttribute('aria-hidden', 'true');

    var pegPositions = [];
    ROW_PEGS.forEach(function (count, rowIndex) {
      var y = TOP_OFFSET + rowIndex * ROW_HEIGHT;
      var rowXs = [];
      for (var i = 0; i < count; i++) {
        var x = BOARD_W / 2 + (i - (count - 1) / 2) * PEG_PITCH;
        rowXs.push(x);
        var circle = document.createElementNS(svgNS, 'circle');
        circle.setAttribute('cx', x);
        circle.setAttribute('cy', y);
        circle.setAttribute('r', 3.5);
        circle.setAttribute('class', 'puko-plinko-peg');
        svg.appendChild(circle);
      }
      pegPositions.push({ y: y, xs: rowXs });
    });
    return { svg: svg, pegPositions: pegPositions };
  }

  // HTML ball: outer wrap carries the trajectory transform (compositor-driven
  // CSS animation); inner core carries the landing squash so the two transforms
  // never fight.
  function buildBall(boardWrap) {
    var wrap = document.createElement('div');
    wrap.className = 'puko-plinko-ball-wrap';
    var core = document.createElement('div');
    core.className = 'puko-plinko-ball-core';
    wrap.appendChild(core);
    boardWrap.appendChild(wrap);
    return wrap;
  }

  function buildSlots(container) {
    var slots = document.createElement('div');
    slots.className = 'puko-plinko-slots';
    for (var i = 0; i < SLOT_VALUES.length; i++) {
      var slot = document.createElement('div');
      slot.className = 'puko-plinko-slot';
      var face = document.createElement('div');
      face.className = 'puko-plinko-slot-face';
      face.textContent = SLOT_VALUES[i];
      slot.appendChild(face);
      slots.appendChild(slot);
    }
    container.appendChild(slots);
    return slots;
  }

  function buildConfetti(boardWrap, originXPct) {
    var layer = document.createElement('div');
    layer.className = 'puko-plinko-confetti';
    layer.style.left = originXPct + '%';
    var colors = ['#ff5a5f', '#ffd166', '#06d6a0', '#118ab2', '#ef476f'];
    for (var i = 0; i < 14; i++) {
      var piece = document.createElement('div');
      piece.className = 'puko-plinko-confetti-piece is-active';
      piece.style.background = colors[i % colors.length];
      var angle = (Math.PI * 2 * i) / 14 + Math.random() * 0.4;
      var dist = 55 + Math.random() * 55;
      piece.style.setProperty('--puko-cx', Math.cos(angle) * dist + 'px');
      piece.style.setProperty('--puko-cy', Math.sin(angle) * dist * 0.8 + 'px');
      piece.style.setProperty('--puko-cr', (Math.random() * 420 - 210) + 'deg');
      layer.appendChild(piece);
    }
    boardWrap.appendChild(layer);
    return layer;
  }

  function shuffledDirs(steps) {
    var dirs = [];
    for (var i = 0; i < steps; i++) dirs.push(i % 2 === 0 ? -1 : 1);
    for (var j = dirs.length - 1; j > 0; j--) {
      var k = Math.floor(Math.random() * (j + 1));
      var tmp = dirs[j]; dirs[j] = dirs[k]; dirs[k] = tmp;
    }
    return dirs;
  }

  // Trajectory in viewBox units. Deflects around the nearest peg each row with
  // a progressive pull toward the winning slot (never a last-moment snap).
  function buildPath(pegPositions, winnerIndex) {
    var steps = pegPositions.length;
    var slotWidth = BOARD_W / SLOT_VALUES.length;
    var targetX = slotWidth * winnerIndex + slotWidth / 2;
    var bias = [0.08, 0.12, 0.18, 0.28, 0.45, 0.65];
    var dirs = shuffledDirs(steps);

    var x = BOARD_W / 2;
    var points = [{ x: x, y: START_Y }];
    for (var i = 0; i < steps; i++) {
      var row = pegPositions[i];
      var nearest = row.xs.reduce(function (best, px) {
        return Math.abs(px - x) < Math.abs(best - x) ? px : best;
      }, row.xs[0]);
      var offset = PEG_PITCH / 2;
      x = nearest + dirs[i] * offset;
      x = x + (targetX - x) * (bias[i] !== undefined ? bias[i] : 0.5);
      points.push({ x: x, y: row.y, dir: dirs[i] });
    }
    points.push({ x: targetX, y: LAND_Y });
    return { points: points, targetX: targetX };
  }

  // Compile the trajectory into a single transform-only @keyframes animation.
  // Keyframe times are sqrt-spaced on y (constant acceleration = real gravity),
  // with mid-segment stops carrying the horizontal peg-deflection overshoot.
  // Ends with a short rebound (up 7 units, back down).
  function compileKeyframes(path, scaleX, scaleY) {
    var pts = path.points;
    var tOf = function (y) { return Math.sqrt((y - START_Y) / (LAND_Y - START_Y)); };
    var stops = [];
    for (var i = 0; i < pts.length; i++) {
      var p = pts[i];
      var t = tOf(p.y) * FALL_MS;
      if (i > 0) {
        var prev = pts[i - 1];
        var midY = (prev.y + p.y) / 2;
        var midT = tOf(midY) * FALL_MS;
        var over = (p.dir || 1) * 4.5;
        stops.push({ t: midT, x: (prev.x + p.x) / 2 + over, y: midY });
      }
      stops.push({ t: t, x: p.x, y: p.y });
    }
    var last = pts[pts.length - 1];
    var reboundStart = FALL_MS;
    stops.push({ t: reboundStart + 95, x: last.x, y: LAND_Y - 7 });
    stops.push({ t: reboundStart + 200, x: last.x, y: LAND_Y });
    var total = reboundStart + 200;

    var css = '';
    for (var s = 0; s < stops.length; s++) {
      var st = stops[s];
      var pct = Math.min(100, (st.t / total) * 100).toFixed(2);
      css += pct + '% { transform: translate(' + (st.x * scaleX).toFixed(1) + 'px,' + (st.y * scaleY).toFixed(1) + 'px); }\n';
    }
    return { css: css, totalMs: total };
  }

  function applyWin(win) {
    try { window.sessionStorage.setItem('pukoPlinkoWon', win.pct); } catch (e) {}
    var tags = document.querySelector('#ContactPopup input[name="contact[tags]"]');
    if (tags) {
      tags.value = win.tag;
      tags.dataset.pukoPlinkoSet = '1';
    }
    var headline = document.querySelector('[data-puko-plinko-claim] .newsletter-popup-title');
    if (headline) headline.textContent = "You've won " + win.pct + '% OFF your first order!';
  }

  function animStyleEl() {
    var el = document.getElementById('puko-plinko-anim');
    if (!el) {
      el = document.createElement('style');
      el.id = 'puko-plinko-anim';
      document.head.appendChild(el);
    }
    return el;
  }

  function animateDrop(refs, path, onDone) {
    var ball = refs.ballEl;
    // Rest position is CSS % (always centered regardless of popup open-animation
    // state); switch to the px coordinate system the keyframes use — measured
    // NOW, when the popup has settled. Same visual spot, no jump.
    ball.style.left = '0px';
    ball.style.top = '0px';
    var rect = refs.svg.getBoundingClientRect();
    var scaleX = (rect.width || BOARD_W) / BOARD_W;
    var scaleY = (rect.height || BOARD_H) / BOARD_H;

    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      ball.style.transform = 'translate(' + (path.targetX * scaleX) + 'px,' + (LAND_Y * scaleY) + 'px)';
      onDone();
      return;
    }

    var name = 'pukoDrop' + Math.floor(Math.random() * 1e6);
    var compiled = compileKeyframes(path, scaleX, scaleY);
    animStyleEl().textContent = '@keyframes ' + name + ' {\n' + compiled.css + '}';

    var finished = false;
    var finish = function () {
      if (finished) return;
      finished = true;
      ball.style.transform = 'translate(' + (path.targetX * scaleX).toFixed(1) + 'px,' + (LAND_Y * scaleY).toFixed(1) + 'px)';
      onDone();
    };
    ball.addEventListener('animationend', finish, { once: true });
    // Safety net in case animationend is swallowed (tab switch, etc.).
    setTimeout(finish, compiled.totalMs + 400);

    ball.style.animation = name + ' ' + compiled.totalMs + 'ms linear forwards';
  }

  function runGame(container, win) {
    var boardWrap = container.querySelector('[data-puko-plinko-boardwrap]');
    var slots = container.querySelectorAll('.puko-plinko-slot');
    var winnerSlot = slots[win.index];
    var refs = container._pukoPlinkoRefs;
    if (!boardWrap || !refs) return;

    var path = buildPath(refs.pegPositions, win.index);

    animateDrop(refs, path, function () {
      refs.ballEl.classList.add('is-landed');
      if (winnerSlot) winnerSlot.classList.add('is-hit');
      buildConfetti(boardWrap, (path.targetX / BOARD_W) * 100);
      applyWin(win);

      setTimeout(function () {
        var gamePanel = container.querySelector('[data-puko-plinko-game-panel]');
        var claimPanel = document.querySelector('[data-puko-plinko-claim]');
        if (gamePanel) gamePanel.classList.add('puko-plinko-hidden');
        if (claimPanel) claimPanel.classList.remove('puko-plinko-hidden');
      }, 900);
    });
  }

  function startGame(container) {
    var claimPanel = document.querySelector('[data-puko-plinko-claim]');
    if (claimPanel) claimPanel.classList.add('puko-plinko-hidden');

    container.innerHTML = '';
    container.classList.remove('puko-plinko-hidden');
    container._pukoPlinkoRefs = null;

    var win = pickWin();

    var header = document.createElement('div');
    header.className = 'puko-plinko-header';
    var title = document.createElement('p');
    title.className = 'puko-plinko-title';
    title.textContent = 'Drop. Bounce. Win.';
    var subtitle = document.createElement('p');
    subtitle.className = 'puko-plinko-subtitle';
    subtitle.textContent = 'Big first-order savings.';
    header.appendChild(title);
    header.appendChild(subtitle);

    var gamePanel = document.createElement('div');
    gamePanel.setAttribute('data-puko-plinko-game-panel', '');

    var boardWrap = document.createElement('div');
    boardWrap.className = 'puko-plinko-board-wrap';
    boardWrap.setAttribute('data-puko-plinko-boardwrap', '');

    var built = buildBoardSVG();
    boardWrap.appendChild(built.svg);
    var ballEl = buildBall(boardWrap);

    gamePanel.appendChild(header);
    gamePanel.appendChild(boardWrap);
    buildSlots(gamePanel);

    var dropBtn = document.createElement('button');
    dropBtn.type = 'button';
    dropBtn.className = 'puko-plinko-drop-btn';
    dropBtn.textContent = 'Drop Ball';
    dropBtn.addEventListener('click', function () {
      if (dropBtn.disabled) return;
      dropBtn.disabled = true;
      dropBtn.classList.add('is-dropping');
      runGame(container, win);
    });
    gamePanel.appendChild(dropBtn);

    container.appendChild(gamePanel);
    container._pukoPlinkoRefs = { svg: built.svg, pegPositions: built.pegPositions, ballEl: ballEl };

    // Rest pose is pure CSS (left:50% / top:4.35%) — no pixel math needed here.
  }

  function init() {
    var popup = document.querySelector(POPUP_SELECTOR);
    if (!popup) return;
    if (!popup.querySelector('[data-puko-plinko]')) return;
    if (isSuccessStateActive()) return;

    var wasOpen = document.body.classList.contains(OPEN_CLASS);

    function onBodyClassChange() {
      var isOpen = document.body.classList.contains(OPEN_CLASS);
      if (isOpen && !wasOpen) {
        wasOpen = true;
        if (isSuccessStateActive()) return;
        var freshPopup = document.querySelector(POPUP_SELECTOR);
        var container = freshPopup && freshPopup.querySelector('[data-puko-plinko]');
        if (!container) return;
        startGame(container);
      } else if (!isOpen) {
        wasOpen = false;
      }
    }

    if (wasOpen) {
      wasOpen = false;
      onBodyClassChange();
    }

    new MutationObserver(onBodyClassChange).observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  ready(init);
})();
